import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays } from "lucide-react";
import { BLOG_POSTS } from "../data";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";

export default function Blog() {
  const [featured, ...rest] = BLOG_POSTS;
  return (
    <div data-testid="blog-page">
      <PageHero
        eyebrow="Tips & Guides"
        title="Simple tech tips, in plain English"
        subtitle="Friendly, easy‑to‑follow guides to help you get more out of your devices — written for real people, not experts."
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        {/* Featured */}
        <Reveal>
          <Link
            to={`/blog/${featured.slug}`}
            data-testid={`blog-featured-${featured.slug}`}
            className="group grid lg:grid-cols-2 gap-8 rounded-[2rem] overflow-hidden border border-border bg-card hover:shadow-xl transition-shadow"
          >
            <img
              src={featured.image}
              alt={featured.title}
              className="w-full h-72 lg:h-full object-cover"
            />
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <span className="rounded-full bg-accent/10 text-accent font-semibold px-3 py-1 text-sm">
                  {featured.category}
                </span>
                <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
                  <CalendarDays className="h-4 w-4" /> {featured.date}
                </span>
              </div>
              <h2 className="font-heading font-extrabold text-2xl lg:text-3xl text-foreground tracking-tight leading-snug group-hover:text-accent transition-colors">
                {featured.title}
              </h2>
              <p className="mt-4 text-muted-foreground text-lg leading-relaxed">{featured.excerpt}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-accent font-bold group-hover:gap-3 transition-all">
                Read the guide <ArrowRight className="h-5 w-5" />
              </span>
            </div>
          </Link>
        </Reveal>

        {/* Grid */}
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {rest.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-post-${p.slug}`}
                className="group flex flex-col h-full rounded-2xl overflow-hidden border border-border bg-card hover:-translate-y-1 hover:shadow-xl transition-transform"
              >
                <img src={p.image} alt={p.title} className="w-full h-52 object-cover" />
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="rounded-full bg-accent/10 text-accent font-semibold px-3 py-1 text-xs">
                      {p.category}
                    </span>
                    <span className="text-muted-foreground text-xs">{p.date}</span>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-foreground leading-snug group-hover:text-accent transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed flex-1">{p.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-accent font-bold">
                    Read more <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABand />
    </div>
  );
}
